/**
 * FileManagerContext — shared folder tree and collections state between Sidebar and ProjectDashboardPage.
 *
 * Avoids duplicate API calls by providing a single source of truth for the
 * active project's folder tree and collections.
 */

import { useState, useCallback, useEffect, type ReactNode } from 'react'
import { FileManagerContext } from './fileManager'
import { useLocation } from 'react-router-dom'
import type { FolderNode, Collection } from '../types/fileManager'
import { assetFolderApi, collectionApi } from '../services/assetFolderApi'


export function FileManagerProvider({ children }: { children: ReactNode }) {
  const location = useLocation()
  const [folderTree, setFolderTree] = useState<FolderNode[]>([])
  const [rootFolderId, setRootFolderId] = useState<number | null>(null)
  const [selectedFolderId, setSelectedFolderId] = useState<number | null>(null)
  const [treeLoading, setTreeLoading] = useState(false)
  const [collections, setCollections] = useState<Collection[]>([])
  const [activeCollectionId, setActiveCollectionId] = useState<number | null>(null)

  // Extract projectId from URL: /projects/:projectId
  const match = location.pathname.match(/^\/projects\/([^/]+)/)
  const activeProjectId = match ? match[1] : null

  const refreshTree = useCallback(async () => {
    if (!activeProjectId) {
      setFolderTree([])
      return
    }
    setTreeLoading(true)
    try {
      const { folders, rootFolderId: rid } = await assetFolderApi.getTree(activeProjectId)
      setFolderTree(folders)
      setRootFolderId(rid)
    } catch {
      setFolderTree([])
      setRootFolderId(null)
    } finally {
      setTreeLoading(false)
    }
  }, [activeProjectId])

  const refreshCollections = useCallback(async () => {
    if (!activeProjectId) {
      setCollections([])
      return
    }
    try {
      const data = await collectionApi.list(activeProjectId)
      setCollections(data.collections)
    } catch {
      setCollections([])
    }
  }, [activeProjectId])

  // Fetch tree and collections when project changes
  useEffect(() => {
    refreshTree()
    refreshCollections()
    setSelectedFolderId(null)
    setActiveCollectionId(null)
  }, [activeProjectId, refreshTree, refreshCollections])

  return (
    <FileManagerContext.Provider
      value={{
        activeProjectId,
        folderTree,
        rootFolderId,
        selectedFolderId,
        setSelectedFolderId,
        refreshTree,
        treeLoading,
        collections,
        activeCollectionId,
        setActiveCollectionId,
        refreshCollections,
      }}
    >
      {children}
    </FileManagerContext.Provider>
  )
}
